import { recordGatewayFailure, recordGatewaySuccess } from "./gateway-health.js";
import { getEadAdminToken } from "./ead-admin.js";

/** Returned from list polls when the server responds 304 Not Modified. */
export type HermesNotModified = { readonly __notModified: true };

export function isHermesNotModified<T>(value: T | HermesNotModified | undefined): value is HermesNotModified {
  return Boolean(value && typeof value === "object" && "__notModified" in value);
}

export type HermesClientOpts = {
  baseUrl: string;
  token?: string;
  /** Per-request timeout in ms (default 30s). Prevents UI stuck on a hung gateway. */
  requestTimeoutMs?: number;
};

export class HermesClient {
  private baseUrl: string;
  private token: string;
  private requestTimeoutMs: number;
  private _stopped = false;
  /** Last failed request detail (HTTP error body or network message) for operator alerts. */
  private _lastError: string | null = null;

  constructor(opts: HermesClientOpts) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, "");
    this.token = opts.token || "";
    this.requestTimeoutMs = opts.requestTimeoutMs ?? 30_000;
  }

  stop(): void {
    this._stopped = true;
  }

  /** Human-readable detail from the last failed `request` call (cleared on success). */
  getLastRequestError(): string | null {
    return this._lastError;
  }

  async request<T = unknown>(method: string, params?: Record<string, unknown>): Promise<T | undefined> {
    const route = this._routeForMethod(method, params);
    if (!route) {
      console.warn(`[HermesClient] No route mapping for RPC method: ${method}`);
      return {} as T;
    }

    const { url, httpMethod, body, query, adminRequest } = route;

    let fullUrl = `${this.baseUrl}${url}`;
    if (query) {
      const qs = Object.entries(query)
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join("&");
      if (qs) fullUrl += `?${qs}`;
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }
    const ifNoneMatch = params?.ifNoneMatch ?? params?.if_none_match;
    if (ifNoneMatch != null && String(ifNoneMatch).trim()) {
      headers["If-None-Match"] = String(ifNoneMatch).trim();
    }
    if (adminRequest) {
      const adminToken = getEadAdminToken();
      if (adminToken) {
        headers["X-EAD-Admin-Token"] = adminToken;
      }
    }

    try {
      console.log(`[HermesClient] ${httpMethod} ${fullUrl}`);
      this._lastError = null;
      const timeoutMs =
        method === "pfm.mindmap"
          ? Math.max(this.requestTimeoutMs, 60_000)
          : method === "chat.history"
            ? Math.max(this.requestTimeoutMs, 90_000)
            : this.requestTimeoutMs;
      const signals: AbortSignal[] = [AbortSignal.timeout(timeoutMs)];
      const userAbort = params?.abortSignal;
      if (userAbort instanceof AbortSignal) {
        signals.push(userAbort);
      }
      const signal =
        typeof AbortSignal.any === "function"
          ? AbortSignal.any(signals)
          : signals[0];
      const resp = await fetch(fullUrl, {
        method: httpMethod,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal,
      });

      if (resp.status === 304) {
        recordGatewaySuccess();
        return { __notModified: true } as T;
      }

      if (!resp.ok) {
        const errBody = await resp.text().catch(() => "");
        let detail = errBody?.slice(0, 400) || "";
        try {
          const j = JSON.parse(errBody) as { error?: { message?: string }; message?: string };
          detail = j?.error?.message || j?.message || detail;
        } catch {
          /* keep text */
        }
        this._lastError = `${resp.status} ${resp.statusText}${detail ? `: ${detail}` : ""}`;
        console.warn(`[HermesClient] ${httpMethod} ${fullUrl} -> ${this._lastError}`);
        recordGatewayFailure();
        return undefined;
      }

      const contentType = resp.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = (await resp.json()) as T & { __etag?: string };
        const etag = resp.headers.get("etag") || resp.headers.get("ETag");
        if (etag && data && typeof data === "object") {
          data.__etag = etag;
        }
        recordGatewaySuccess();
        return data as T;
      }
      this._lastError = `Expected JSON but got: ${contentType || "(no content-type)"}`;
      console.warn(`[HermesClient] ${httpMethod} ${fullUrl} -> ${this._lastError}`);
      return undefined;
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        return undefined;
      }
      const timeoutMs =
        method === "pfm.mindmap"
          ? Math.max(this.requestTimeoutMs, 60_000)
          : method === "chat.history"
            ? Math.max(this.requestTimeoutMs, 90_000)
            : this.requestTimeoutMs;
      const msg =
        err instanceof Error && err.name === "TimeoutError"
          ? `Gateway request timed out after ${timeoutMs / 1000}s (is Hermes running on ${this.baseUrl}?)`
          : err instanceof Error
            ? err.message
            : String(err);
      this._lastError = msg;
      console.warn(`[HermesClient] ${httpMethod} ${fullUrl} failed:`, err);
      recordGatewayFailure();
      return undefined;
    }
  }

  private _routeForMethod(
    method: string,
    params?: Record<string, unknown>,
  ): { url: string; httpMethod: string; body?: unknown; query?: Record<string, unknown>; adminRequest?: boolean } | null {
    const id =
      typeof params?.id === "string" && params.id.trim() ? params.id.trim() : undefined;
    const templateId =
      typeof params?.templateId === "string" && params.templateId.trim()
        ? params.templateId.trim()
        : undefined;
    const executionId =
      typeof params?.executionId === "string" && params.executionId.trim()
        ? params.executionId.trim()
        : undefined;

    switch (method) {
      // Templates
      case "templates.list":
        return { url: "/v1/projects/templates", httpMethod: "GET" };
      case "templates.interactiveSidebar":
        return { url: "/v1/projects/templates/interactive-sidebar", httpMethod: "GET" };
      case "templates.get":
        return {
          url: `/v1/projects/templates/${id || templateId}`,
          httpMethod: "GET",
          query:
            params?.lite === true || params?.lite === 1 || params?.lite === "1"
              ? { lite: "1" }
              : undefined,
        };
      case "templates.create":
        return { url: "/v1/projects/templates", httpMethod: "POST", body: this._templateCreateBody(params) };
      case "templates.update":
        return { url: `/v1/projects/templates/${id || templateId}`, httpMethod: "PATCH", body: this._templateUpdateBody(params) };
      case "templates.delete":
        return { url: `/v1/projects/templates/${id || templateId}`, httpMethod: "DELETE" };
      case "templates.setActive":
        return { url: `/v1/projects/templates/${id || templateId}/activate`, httpMethod: "POST" };
      case "templates.setCanonicalPfm":
        return {
          url: `/v1/projects/templates/${id || templateId}/pfm/canonical`,
          httpMethod: "POST",
          body: {
            executionId: params?.executionId,
            source: params?.source ?? "operator",
            rationale: params?.rationale,
          },
        };

      // Policy skills (EAD run rules)
      case "policySkills.list":
        return { url: "/v1/projects/policy-skills", httpMethod: "GET" };
      case "policySkills.get": {
        const skillKey = String(params?.skillKey || params?.id || "");
        return { url: `/v1/projects/policy-skills/${encodeURIComponent(skillKey)}`, httpMethod: "GET" };
      }
      case "policySkills.update": {
        const skillKey = String(params?.skillKey || params?.id || "");
        const configJson = params?.configJson ?? params?.config_json;
        return {
          url: `/v1/projects/policy-skills/${encodeURIComponent(skillKey)}`,
          httpMethod: "PUT",
          body: {
            displayName: params?.displayName,
            note: params?.note,
            bodyMarkdown: params?.bodyMarkdown,
            sortOrder: params?.sortOrder,
            ...(configJson !== undefined ? { configJson } : {}),
          },
          adminRequest: true,
        };
      }
      case "policySkills.generateEadScript": {
        const skillKey = String(params?.skillKey || params?.id || "");
        return {
          url: `/v1/projects/policy-skills/${encodeURIComponent(skillKey)}/generate-ead-script`,
          httpMethod: "POST",
          body: {
            generatePrompt: params?.generatePrompt,
            savePrompt: params?.savePrompt ?? false,
            persist: params?.persist ?? true,
          },
          adminRequest: true,
        };
      }
      case "policySkills.delete": {
        const skillKey = String(params?.skillKey || params?.id || "");
        return {
          url: `/v1/projects/policy-skills/${encodeURIComponent(skillKey)}`,
          httpMethod: "DELETE",
        };
      }
      case "policySkills.seed":
        return { url: "/v1/projects/policy-skills/seed", httpMethod: "POST" };

      // EAD Workflow Rules
      case "ead.workflow_rules.list":
        return { url: "/v1/ead-workflow-rules", httpMethod: "GET" };
      case "ead.workflow_rules.get": {
        const ruleKey = String(params?.ruleKey || params?.id || "");
        return { url: `/v1/ead-workflow-rules/${encodeURIComponent(ruleKey)}`, httpMethod: "GET" };
      }
      case "ead.workflow_rules.update": {
        const ruleKey = String(params?.ruleKey || params?.id || "");
        const configJson = params?.configJson ?? params?.config_json;
        return {
          url: `/v1/ead-workflow-rules/${encodeURIComponent(ruleKey)}`,
          httpMethod: "PUT",
          body: {
            ruleName: params?.ruleName,
            ruleDescription: params?.ruleDescription,
            bodyMarkdown: params?.bodyMarkdown,
            eadScriptGrammar: params?.eadScriptGrammar,
            mermaidDiagram: params?.mermaidDiagram,
            sortNumber: params?.sortNumber,
            enabled: params?.enabled,
            modifiedBy: params?.modifiedBy,
            ...(configJson !== undefined ? { configJson } : {}),
          },
          adminRequest: true,
        };
      }
      case "ead.workflow_rules.create": {
        const configJson = params?.configJson ?? params?.config_json;
        return {
          url: "/v1/ead-workflow-rules",
          httpMethod: "POST",
          body: {
            ruleKey: params?.ruleKey,
            ruleName: params?.ruleName,
            ruleDescription: params?.ruleDescription,
            bodyMarkdown: params?.bodyMarkdown,
            eadScriptGrammar: params?.eadScriptGrammar,
            mermaidDiagram: params?.mermaidDiagram,
            sortNumber: params?.sortNumber ?? 0,
            enabled: params?.enabled ?? true,
            createdBy: params?.createdBy ?? "api-user",
            ...(configJson !== undefined ? { configJson } : {}),
          },
          adminRequest: true,
        };
      }
      case "ead.workflow_rules.delete": {
        const ruleKey = String(params?.ruleKey || params?.id || "");
        return {
          url: `/v1/ead-workflow-rules/${encodeURIComponent(ruleKey)}`,
          httpMethod: "DELETE",
          adminRequest: true,
        };
      }
      case "ead.workflow_rules.reorder": {
        return {
          url: "/v1/ead-workflow-rules/reorder",
          httpMethod: "POST",
          body: {
            reorderData: params?.reorderData ?? [],
          },
          adminRequest: true,
        };
      }
      case "ead.workflow_rules.generateEadScript": {
        const ruleKey = String(params?.ruleKey || params?.id || "");
        return {
          url: `/v1/ead-workflow-rules/${encodeURIComponent(ruleKey)}/generate-ead-script`,
          httpMethod: "POST",
          body: {
            generatePrompt: params?.generatePrompt,
            savePrompt: params?.savePrompt ?? false,
            persist: params?.persist ?? true,
          },
          adminRequest: true,
        };
      }
      case "ead.workflow_rules.seed":
        return { url: "/v1/ead-workflow-rules/seed", httpMethod: "POST", adminRequest: true };

      // Executions
      case "executions.list": {
        const listQuery: Record<string, string> = {};
        if (templateId) listQuery.template_id = templateId;
        const listLimit = params?.limit ?? params?.listLimit;
        if (listLimit != null && String(listLimit).trim()) {
          listQuery.limit = String(listLimit);
        }
        return {
          url: "/v1/projects/executions",
          httpMethod: "GET",
          query: Object.keys(listQuery).length ? listQuery : undefined,
        };
      }
      case "executions.active":
        return { url: "/v1/projects/executions/active", httpMethod: "GET" };
      case "executions.get":
        return {
          url: `/v1/projects/executions/${id || executionId}`,
          httpMethod: "GET",
          query:
            params?.skipBackfill === true || params?.skip_backfill === true
              ? { skip_backfill: "1" }
              : undefined,
        };
      case "executions.delete":
        return { url: `/v1/projects/executions/${id || executionId}`, httpMethod: "DELETE" };
      case "executions.invalidate":
        return {
          url: `/v1/projects/executions/${id || executionId}/invalidate`,
          httpMethod: "POST",
          body: { reason: params?.reason },
        };
      case "executions.setReportingActivity":
        return {
          url: `/v1/projects/executions/${id || executionId}/reporting-activity`,
          httpMethod: "POST",
          body: {
            active: params?.active,
            status: params?.status,
          },
        };
      case "executions.setLearning":
        return {
          url: `/v1/projects/executions/${id || executionId}/learning`,
          httpMethod: "POST",
          body: {
            validForContinuousLearning: params?.validForContinuousLearning,
            reason: params?.reason,
          },
        };
      case "executions.setAnswerLanguage":
        return {
          url: `/v1/projects/executions/${id || executionId}/answer-language`,
          httpMethod: "POST",
          body: {
            ai_answer_language: params?.aiAnswerLanguage,
          },
        };
      case "executions.run":
        return { url: "/v1/projects/executions/run", httpMethod: "POST", body: this._executionRunBody(params) };
      case "executions.cancel":
        return { url: `/v1/projects/executions/${id || executionId}/cancel`, httpMethod: "POST", body: this._cancelBody(params) };
      case "executions.pause":
        return { url: `/v1/projects/executions/${id || executionId}/pause`, httpMethod: "POST" };
      case "executions.resume":
        return { url: `/v1/projects/executions/${id || executionId}/resume`, httpMethod: "POST" };
      case "executions.extendBudget":
        return {
          url: `/v1/projects/executions/${id || executionId}/extend-budget`,
          httpMethod: "POST",
          body: this._extendBudgetBody(params),
        };
      case "pfmNodeReports.save":
        return {
          url: `/v1/projects/executions/${executionId}/artifacts/node-report`,
          httpMethod: "POST",
          body: {
            node_key: params?.nodeKey,
            title: params?.title,
            content: params?.content,
          },
        };
      case "pfmNodeReports.get":
        return {
          url: `/v1/projects/executions/${executionId}/artifacts/node-report`,
          httpMethod: "GET",
          query: {
            node_key: params?.nodeKey,
            selected_node_only: params?.selectedNodeOnly ? "1" : undefined,
          },
        };
      case "pfmNodeReports.getWithBaseline":
        return {
          url: `/v1/projects/executions/${executionId}/artifacts/node-report`,
          httpMethod: "GET",
          query: {
            node_key: params?.nodeKey,
          },
        };
      case "executions.activityLog":
        return {
          url: `/v1/projects/executions/${executionId || id}/activity-log`,
          httpMethod: "GET",
          query: {
            limit: params?.limit,
            offset: params?.offset,
            since_seq: params?.sinceSeq ?? params?.since_seq,
          },
        };

      // Agent-authored PFM tree pipeline
      case "pfm.tree":
        return {
          url: `/v1/projects/executions/${executionId}/pfm/tree`,
          httpMethod: "GET",
        };
      case "pfm.deliveryStatus":
        return {
          url: `/v1/projects/executions/${executionId}/pfm/delivery-status`,
          httpMethod: "GET",
        };
      case "pfm.deliveryValidation":
        return {
          url: `/v1/projects/executions/${executionId}/pfm/delivery-validation`,
          httpMethod: "GET",
        };
      case "pfm.qualityGate":
        return {
          url: `/v1/projects/executions/${executionId}/pfm/quality-gate`,
          httpMethod: "GET",
        };
      case "pfm.requestSnapshot": {
        // Delivery/backfill import only — NOT for Run Explore mindmap display/reload (use pfm.mindmap).
        const rb: Record<string, unknown> = {};
        if (params?.materialize !== undefined) rb.materialize = params.materialize;
        if (params?.refreshFromDelivery === true) rb.refreshFromDelivery = true;
        if (params?.promoteTemplateCanonical !== undefined) {
          rb.promoteTemplateCanonical = params.promoteTemplateCanonical;
        }
        return {
          url: `/v1/projects/executions/${executionId}/pfm/request-snapshot`,
          httpMethod: "POST",
          body: Object.keys(rb).length ? rb : undefined,
        };
      }
      case "pfm.lineageGaps":
        return {
          url: `/v1/projects/executions/${executionId}/pfm/lineage-gaps`,
          httpMethod: "GET",
        };
      case "pfm.backfillLineage":
        return {
          url: `/v1/projects/executions/${executionId}/pfm/backfill-lineage`,
          httpMethod: "POST",
          body: params?.promoteTemplateCanonical !== undefined
            ? { promoteTemplateCanonical: params.promoteTemplateCanonical }
            : undefined,
        };
      case "pfm.mindmap":
        return {
          url: `/v1/projects/executions/${executionId}/pfm/mindmap`,
          httpMethod: "GET",
          query: {
            scope: params?.scope,
            node_key: params?.nodeKey,
            depth: params?.depth,
            read_execution_id: params?.readExecutionId,
          },
        };
      case "pfm.viewGet":
        return {
          url: `/v1/projects/executions/${executionId}/pfm/view`,
          httpMethod: "GET",
        };
      case "pfm.skills.list":
        return {
          url: `/v1/projects/executions/${executionId}/pfm/skills`,
          httpMethod: "GET",
        };
      case "pfm.skills.create":
        return {
          url: `/v1/projects/executions/${executionId}/pfm/skills`,
          httpMethod: "POST",
        };
      case "pfm.viewPut":
        return {
          url: `/v1/projects/executions/${executionId}/pfm/view`,
          httpMethod: "PUT",
          body: {
            node_path: params?.nodePath,
            selected_node_key: params?.selectedNodeKey,
            view_scope: params?.viewScope,
            depth_cap: params?.depthCap,
            pfm_tree_version: params?.pfmTreeVersion,
          },
        };
      case "pfm.evaluateCanonical":
        return {
          url: `/v1/projects/executions/${executionId}/pfm/evaluate-canonical`,
          httpMethod: "POST",
          body: { force: params?.force === true },
        };
      case "pfm.optimizeMindmap.preflight":
        return {
          url: `/v1/projects/executions/${executionId}/pfm/optimize-mindmap/preflight`,
          httpMethod: "GET",
        };
      case "pfm.optimizeMindmap":
        return {
          url: `/v1/projects/executions/${executionId}/pfm/optimize-mindmap`,
          httpMethod: "POST",
        };

      // Chat
      case "sessions.create":
        return { url: "/v1/chat/sessions", httpMethod: "POST", body: { session_key: params?.key, display_name: params?.label } };
      case "chat.history":
        return {
          url: "/v1/chat/history",
          httpMethod: "GET",
          query: {
            session_key: params?.sessionKey || params?.key,
            limit: String(params?.limit || 100),
            offset: params?.offset != null ? String(params.offset) : undefined,
          },
        };
      case "chat.inject":
        return { url: "/v1/chat/inject", httpMethod: "POST", body: { session_key: params?.sessionKey, role: params?.role || "system", content: params?.content, label: params?.label } };
      case "chat.send":
        return { url: "/v1/chat/send", httpMethod: "POST", body: { session_key: params?.sessionKey, content: params?.message || params?.content, deliver: params?.deliver ?? true, idempotency_key: params?.idempotencyKey || params?.idempotencyKey } };
      case "chat.status":
        return {
          url: "/v1/chat/status",
          httpMethod: "GET",
          query: { session_key: params?.sessionKey || params?.key },
        };
      case "chat.abort":
        return { url: "/v1/chat/abort", httpMethod: "POST", body: { session_key: params?.sessionKey, reason: params?.reason } };

      // Chat Generation (New)
      case "chat.generate":
        return { url: "/v1/chat/generate", httpMethod: "POST", body: this._chatGenerateBody(params) };
      case "chat.export":
        return { url: "/v1/chat/export", httpMethod: "POST", body: { session_id: params?.sessionId, format: params?.format || "md" } };

      default:
        return null;
    }
  }

  private _templateCreateBody(params?: Record<string, unknown>): Record<string, unknown> {
    if (!params) return {};
    return {
      name: params.name,
      description: params.description,
      explore_type: params.exploreType,
      target_url: params.targetUrl,
      ai_prompt: params.aiPrompt,
      auth_mode: params.authMode,
      auth_login_url: params.authLoginUrl,
      auth_session_profile: params.authSessionProfile,
      auth_instructions: params.authInstructions,
      time_budget_minutes: params.timeBudgetMinutes,
      created_by: params.createdBy || "operator",
      last_modified_by: params.lastModifiedBy || "operator",
    };
  }

  private _templateUpdateBody(params?: Record<string, unknown>): Record<string, unknown> {
    if (!params) return {};
    const body: Record<string, unknown> = {};
    const fieldMap: Record<string, string> = {
      name: "name",
      description: "description",
      targetUrl: "target_url",
      aiPrompt: "ai_prompt",
      authMode: "auth_mode",
      authLoginUrl: "auth_login_url",
      authSessionProfile: "auth_session_profile",
      authInstructions: "auth_instructions",
      exploreType: "explore_type",
      timeBudgetMinutes: "time_budget_minutes",
      costBudgetDollars: "cost_budget_dollars",
      pfmNodes: "pfm_nodes",
    };
    for (const [from, to] of Object.entries(fieldMap)) {
      if (params[from] !== undefined) {
        body[to] = params[from];
      }
    }
    return body;
  }

  /**
   * POST /v1/projects/executions/run body.
   * When `inherit_pfm` is true, the gateway should resolve the latest execution for the same
   * `template_id` that passes the PFM inheritance eligibility rules (see pfm-run-eligibility.ts),
   * copy node-report artifacts into the new execution, seed the new chat session with the source
   * PFM mindmap cache envelope, and return `inherited_from_execution_id` on the response when set.
   */
  private _executionRunBody(params?: Record<string, unknown>): Record<string, unknown> {
    if (!params) return {};
    const body: Record<string, unknown> = {
      template_id: params.templateId,
      name: params.name,
      description: params.description,
      explore_type: params.exploreType,
      target_url: params.targetUrl,
      ai_prompt: params.aiPrompt,
      ai_answer_language: params.aiAnswerLanguage,
      time_budget_minutes: params.timeBudgetMinutes,
      cost_budget_dollars: params.costBudgetDollars,
      show_local_browser: params.showLocalBrowser,
      run_purpose: params.runPurpose,
      learning_mode: params.learningMode,
      contributes_to_learning: params.contributesToLearning,
      evidence_source: params.evidenceSource,
      learning_exclusion_reason: params.learningExclusionReason,
      valid_for_data_reporting_training: params.validForDataReportingTraining,
      invalid_for_data_reporting_training_reason: params.invalidForDataReportingTrainingReason,
    };
    const inheritPfm = params.inheritPfm;
    if (inheritPfm === false) {
      body.inherit_pfm = false;
    } else {
      body.inherit_pfm = true;
    }
    const inheritFrom = params.inheritFromExecutionId;
    if (typeof inheritFrom === "string" && inheritFrom.trim()) {
      body.inherit_from_execution_id = inheritFrom.trim();
    }
    return body;
  }

  private _cancelBody(params?: Record<string, unknown>): Record<string, unknown> {
    if (!params) return {};
    return {
      stop_kind: params.mode || "cancel",
      reason: params.reason || "",
    };
  }

  private _extendBudgetBody(params?: Record<string, unknown>): Record<string, unknown> {
    if (!params) return {};
    return {
      extend_minutes: params.extendMinutes ?? params.minutes,
    };
  }

  private _chatGenerateBody(params?: Record<string, unknown>): Record<string, unknown> {
    if (!params) return {};
    return {
      template_id: params.templateId,
      focus_area: params.focusArea,
      max_length: params.maxLength || 20,
      style: params.style || "conversational",
      include_context: params.includeContext ?? true,
    };
  }
}

export function deriveDefaultHermesUrl(): { pageUrl: string; effectiveUrl: string } {
  const proto = location.protocol === "https:" ? "https" : "http";
  const configured =
    typeof window !== "undefined" &&
    typeof window.__HERMES_CONTROL_UI_BASE_PATH__ === "string" &&
    window.__HERMES_CONTROL_UI_BASE_PATH__.trim();

  // If there's a configured base path, use it for the effective URL
  if (configured) {
    const basePath = configured.startsWith("/") ? configured : `/${configured}`;
    const pageUrl = `${proto}://${location.host}${basePath}`;
    return { pageUrl, effectiveUrl: basePath };
  }

  // Otherwise use the current host
  const pageUrl = `${proto}://${location.host}`;
  return { pageUrl, effectiveUrl: pageUrl };
}
